import React, { useState } from "react";
import { Button, TextField, Box, Typography } from "@mui/material";
import Modal from "react-bootstrap/Modal";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import config from "../config";
import { DAPP_NAME, TransactionName } from "../constants";
import { ExecuteSubmitTransaction, formattedErrorMessage } from "../utils";
import MyVerticallyCenteredModal from "./MyVerticallyCenteredModal";

const getErrorMessage = (message) => {
  try {
    // Remove "Error:" prefix if it exists
    if (message.startsWith("Error:")) {
      message = message.replace("Error: ", "");
    }

    // Try to parse as JSON first (for structured errors)
    if (message.startsWith("{")) {
      const parsedError = JSON.parse(message);

      // Check for specific error message and return custom messages
      if (parsedError?.message?.error === "insufficient balance") {
        return "Not Authorized!";
      }

      // Return the error message from the parsed object if available
      return parsedError.message?.error || "Unknown Error";
    }

    // Handle plain text errors
    if (message === "insufficient balance") {
      return "Not Authorized!";
    }

    // If no mapping, return the original message
    return message;
  } catch (err) {
    // If parsing fails, return the original message or a fallback
    return message || "An unexpected error occurred.";
  }
};

const Whitelist = ({ goBack }) => {
  // Add goBack prop
  const [merchantId, setMerchantId] = useState("");
  const [responseMessage, setResponseMessage] = useState("");
  const [modalShow, setModalShow] = useState(false);
  const [inputError, setInputError] = useState(false); // Track input error state

  const validateInput = () => {
    if (!merchantId) {
      setInputError(true);
      return false;
    }
    setInputError(false);
    return true;
  };

  const handleWhitelist = async () => {
    if (!validateInput()) return; // Prevent execution if input is invalid
    try {
      const transactionParams = [merchantId];
      console.log(
        "...Whitelisting:",
        TransactionName.WHITELIST,
        transactionParams
      );
      const [res, errMsg] = await ExecuteSubmitTransaction(
        config.CHANNEL_NAME,
        config.CHAINCODE_NAME,
        TransactionName.WHITELIST,
        transactionParams
      );
      console.log("...Whitelisting:", res, errMsg);
      if (res !== undefined) {
        setResponseMessage(
          res !== undefined
            ? `${res}`
            : `Error: ${formattedErrorMessage(errMsg)}`
        );
      } else {
        throw new Error(formattedErrorMessage(errMsg));
      }
    } catch (error) {
      console.log("Error in handleWhitelist:", error);
      setResponseMessage("Error: An error occurred during the transaction.");
    } finally {
      setModalShow(true);
    }
  };

  const handleBlacklist = async () => {
    if (!validateInput()) return; // Prevent execution if input is invalid
    try {
      const transactionParams = [merchantId];

      const [res, errMsg] = await ExecuteSubmitTransaction(
        config.CHANNEL_NAME,
        config.CHAINCODE_NAME,
        TransactionName.BLACKLIST,
        transactionParams
      );
      if (res !== undefined) {
        setResponseMessage(
          res !== undefined
            ? `${res}`
            : `Error: ${formattedErrorMessage(errMsg)}`
        );
      } else {
        throw new Error(formattedErrorMessage(errMsg));
      }
    } catch (error) {
      console.log("Error in handleBlacklist:", error);
      setResponseMessage("Error: An error occurred during the transaction.");
    } finally {
      setModalShow(true);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        width: "100%",
        padding: "20px",
      }}
    >
      {/* Back Button */}
      <Button
        onClick={goBack} // Call goBack on click
        startIcon={<ArrowBackIcon />}
        sx={{
          alignSelf: "flex-start",
          marginBottom: "20px",
          textTransform: "none",
        }}
      >
        Back
      </Button>

      <Typography
        variant="h5"
        sx={{ fontWeight: "bold", marginBottom: "20px" }}
      >
        Whitelist / Blacklist Request
      </Typography>

      <TextField
        placeholder="Enter User's Address"
        variant="outlined"
        value={merchantId}
        onChange={(e) => setMerchantId(e.target.value.toLowerCase())}
        fullWidth
        error={inputError} // Apply error state
        helperText={inputError ? "Required" : ""}
        FormHelperTextProps={{
          sx: {
            backgroundColor: "transparent", // Remove any background color
            marginLeft: 0, // Align the helper text with the input field
            color: "red", // Set text color to red for visibility
            textTransform: "lowercase",
          },
        }}
        sx={{
          backgroundColor: "transparent",
          borderRadius: "8px",
          marginBottom: "20px",
          maxWidth: "400px",
          "& .MuiOutlinedInput-root": {
            borderRadius: "8px",
            "& fieldset": {
              borderColor: "#E0E0E0",
            },
            "&:hover fieldset": {
              borderColor: "#E0E0E0",
            },
            "&.Mui-focused fieldset": {
              borderColor: "#1976D2",
            },
          },
        }}
      />

      {/* Whitelist User Button */}
      <Button
        variant="contained"
        onClick={handleWhitelist}
        sx={{
          marginBottom: "10px",
          backgroundColor: "#0055FF",
          borderRadius: "8px",
          height: "45px",
          width: "100%", // Ensures button width matches TextField
          maxWidth: "400px",
          textTransform: "none",
          fontSize: "16px",
          fontWeight: "bold",
          "&:hover": {
            backgroundColor: "#0044CC",
          },
        }}
      >
        Whitelist User
      </Button>

      {/* Blacklist User Button */}
      <Button
        variant="outlined"
        onClick={handleBlacklist}
        sx={{
          borderColor: "#FF0000",
          color: "#FF0000",
          borderRadius: "8px",
          height: "45px",
          width: "100%", // Ensures button width matches TextField
          maxWidth: "400px",
          textTransform: "none",
          fontSize: "16px",
          fontWeight: "bold",
          "&:hover": {
            borderColor: "#FF3333",
            backgroundColor: "rgba(255, 0, 0, 0.04)",
          },
        }}
      >
        Blacklist User
      </Button>

      {/* Modal for response */}
      <MyVerticallyCenteredModal
        show={modalShow}
        onHide={() => setModalShow(false)}
        message={responseMessage}
        getErrorMessage={getErrorMessage}
      />
    </Box>
  );
};

export default Whitelist;
